import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { lenderFirmsGetFirmFcaData, FcaResponseResult, FirmFcaData } from '@pmp/api';
import { notification } from '@ohpen/enso-ui';
import { Form } from 'antd';

import { TabProps } from '../interfaces';
import { TabContent } from './TabContent';
import { RegistrationDetails } from './RegistrationDetails';
import { ContactDetails } from './ContactDetails';
import { TradingNames } from './TradingNames';
import { Payments } from './Payments';
import { Packaging } from './Packaging';
import { Status } from './Status';
import { showFcaNotification } from '../utils';

export const FirmDetailsTab: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const [fcaModifiedFields, setFcaModifiedFields] = useState<FirmFcaData | null | undefined>(null);
  const watchedOperations: string[] | undefined = Form.useWatch(['registrationDetails', 'operations'], form);
  const showPackaging = watchedOperations?.includes('PACKAGE') ?? false;

  const populateData = useCallback(
    (frn: number): Promise<void> => {
      return lenderFirmsGetFirmFcaData({ path: { frn } })
        .then(({ data }) => {
          const { result, registrationDetails, contactDetails, tradingNames } = data;
          if (!result) return;
          if (result === FcaResponseResult.OK || result === FcaResponseResult.PARTIAL_DATA) {
            const fcaNames = tradingNames ?? [];
            const fcaNameSet = new Set(fcaNames.map(tn => tn.name?.trim().toLowerCase()));
            const existing: Record<string, unknown>[] = form?.getFieldValue('tradingNames') ?? [];
            const existingNameSet = new Set(existing.map(row => (row.name as string | undefined)?.trim().toLowerCase() ?? ''));

            const updatedExisting = existing.map(row => ({
              ...row,
              isActive: fcaNameSet.has((row.name as string | undefined)?.trim().toLowerCase() ?? ''),
            }));
            const newFromFca = fcaNames
              .filter(tn => !existingNameSet.has(tn.name?.trim().toLowerCase() ?? ''))
              .map(tn => ({ ...tn, isActive: true }));

            form?.setFieldsValue({
              registrationDetails: {
                ...registrationDetails,
                type: registrationDetails?.type ?? 'NOT_FCA_REGISTERED',
              },
              contactDetails: { ...contactDetails },
              tradingNames: [...updatedExisting, ...newFromFca],
            });
            
            setFcaModifiedFields(data);
          }

          if (result === FcaResponseResult.REFERENCE_NOT_FOUND) {
            form?.setFieldsValue({
              registrationDetails: {
                ...registrationDetails,
                type: registrationDetails?.type ?? 'NOT_FCA_REGISTERED',
              },
            });
          }

          showFcaNotification(result, t, notification());
        })
        .catch(() => {
          notification().error({ message: t('global.errors.unexpected') });
        });
    },
    [form, t],
  );

  const removeFcaModifiedField = useCallback((key: keyof FirmFcaData, nestedKey?: string) => {
    setFcaModifiedFields(prev => {
      const next = { ...prev };
      if (nestedKey && next[key]) {
        const nested = { ...(next[key] as Record<string, unknown>) };
        delete nested[nestedKey];
        next[key] = nested as never;
      } else {
        delete next[key];
      }
      return next;
    });
  }, []);

  return (
    <TabContent>
      <RegistrationDetails
        form={form}
        mode={mode}
        selectsOptions={selectsOptions}
        populateFcaData={populateData}
        fcaModifiedFields={fcaModifiedFields}
        removeFcaModifiedField={removeFcaModifiedField}
      />
      <ContactDetails
        form={form}
        mode={mode}
        selectsOptions={selectsOptions}
        fcaModifiedFields={fcaModifiedFields}
        removeFcaModifiedField={removeFcaModifiedField}
      />
      <Payments form={form} mode={mode} selectsOptions={selectsOptions} />
      {showPackaging && <Packaging form={form} mode={mode} selectsOptions={selectsOptions} />}
      <Status form={form} mode={mode} selectsOptions={selectsOptions} />
      <TradingNames
        form={form}
        mode={mode}
        selectsOptions={selectsOptions}
        fcaModifiedFields={fcaModifiedFields}
        removeFcaModifiedField={removeFcaModifiedField}
      />
    </TabContent>
  );
};
